import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { cn, formatCurrency } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";

type TimeRange = "monthly" | "quarterly" | "yearly";

export default function SpendAnalysisChart() {
  const [timeRange, setTimeRange] = useState<TimeRange>("monthly");
  
  // Fetch real tenant-specific data from the API
  const { data: chartData, isLoading, error } = useQuery({
    queryKey: ['/api/dashboard/spend-analysis', timeRange],
    queryFn: async () => {
      return await apiRequest("GET", `/api/dashboard/spend-analysis?timeRange=${timeRange}`);
    }
  });

  const formatYAxis = (value: number) => {
    return `$${(value / 1000).toFixed(0)}k`;
  };

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 shadow-sm rounded-md">
          <p className="font-medium text-slate-800">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={`item-${index}`} style={{ color: entry.color }} className="text-sm mt-1">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <CardTitle className="text-lg font-semibold">
          {timeRange === "monthly" 
            ? "Monthly Spend Analysis" 
            : timeRange === "quarterly" 
              ? "Quarterly Spend Analysis" 
              : "Yearly Spend Analysis"}
        </CardTitle>
        <div className="flex space-x-2">
          <Button 
            size="sm" 
            variant={timeRange === "monthly" ? "default" : "outline"}
            onClick={() => setTimeRange("monthly")}
            className="text-xs"
            disabled={isLoading}
          >
            Monthly
          </Button>
          <Button 
            size="sm" 
            variant={timeRange === "quarterly" ? "default" : "outline"}
            onClick={() => setTimeRange("quarterly")}
            className="text-xs"
            disabled={isLoading}
          >
            Quarterly
          </Button>
          <Button 
            size="sm" 
            variant={timeRange === "yearly" ? "default" : "outline"}
            onClick={() => setTimeRange("yearly")}
            className="text-xs"
            disabled={isLoading}
          >
            Yearly
          </Button>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="h-64">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full w-full space-y-4">
              <Skeleton className="h-[180px] w-full rounded-md" />
              <div className="space-y-2 w-full">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-[80%]" />
              </div>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full w-full">
              <p className="text-destructive">Error loading spend analysis data</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="mt-2"
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis 
                  dataKey={timeRange === "monthly" ? "month" : timeRange === "quarterly" ? "quarter" : "year"} 
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis 
                  tickFormatter={formatYAxis} 
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={customTooltip} />
                <Legend />
                {timeRange !== "yearly" ? (
                  <>
                    <Line
                      type="monotone"
                      dataKey="thisYear"
                      name="This Year"
                      stroke="#2563eb"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="lastYear"
                      name="Last Year"
                      stroke="#94a3b8"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      strokeDasharray="5 5"
                    />
                  </>
                ) : (
                  <Line
                    type="monotone"
                    dataKey="value"
                    name="Annual Spend"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
